import { Injectable } from '@angular/core';
import { ActionRequiredState, ActionTypeModel, FeatureFlagConfigurationState, UpdateGeaIdUser, UserActionRequired, UserMembershipState, UserState } from '../../../shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, concatMap, delay, distinctUntilChanged, filter, first, map, mergeMap, of, pipe, startWith, switchMap, take, tap } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "../../../shared";
import * as i3 from "../services/app.service";
import * as i4 from "@ngx-translate/core";
import * as i5 from "../../../complex-components/basic-grid";
import * as i6 from "@angular/router";
import * as i7 from "../../../basic-features";
import * as i8 from "../../../shared/services/sse-stream.service";
import * as i9 from "../../notifications-v2";
import * as i10 from "./action-required.service";
import * as i11 from "./system-message.service";
export class HeaderNgxsService {
  store;
  permissionService;
  featureFlagService;
  resourcesService;
  appService;
  userService;
  notificationService;
  translate;
  destroyed;
  gaService;
  router;
  errorHandlerService;
  sseService;
  notificationsV2Service;
  actionRequiredService;
  systemMessageService;
  expirationTime$;
  userName = '';
  appList;
  tokenReady = false;
  availableApps = '';
  currentUserContext;
  tempUserMembership = {
    id: '',
    name: '',
    roles: ['']
  };
  userPermissions = [];
  constructor(store, permissionService, featureFlagService, resourcesService, appService, userService, notificationService, translate, destroyed, gaService, router, errorHandlerService, sseService, notificationsV2Service, actionRequiredService, systemMessageService) {
    this.store = store;
    this.permissionService = permissionService;
    this.featureFlagService = featureFlagService;
    this.resourcesService = resourcesService;
    this.appService = appService;
    this.userService = userService;
    this.notificationService = notificationService;
    this.translate = translate;
    this.destroyed = destroyed;
    this.gaService = gaService;
    this.router = router;
    this.errorHandlerService = errorHandlerService;
    this.sseService = sseService;
    this.notificationsV2Service = notificationsV2Service;
    this.actionRequiredService = actionRequiredService;
    this.systemMessageService = systemMessageService;
    this.expirationTime$ = this.store.select(UserState.expirationTime);
  }
  initializeNgxsData(canOpenBurgerMenu, googleAnalyticsActivated) {
    this.setupUsername(googleAnalyticsActivated);
    this.setupAppsAndLocals(canOpenBurgerMenu);
    this.setupAssignUserContext();
    this.setupFeatureFlagConfig();
    this.setupUserInformations();
  }
  initializeNonAdminData() {
    this.store.select(ActionRequiredState.actionRequiredState).subscribe(state => {
      this.actionRequiredService.handleMessageCreation(state);
    });
    this.setupNotificationSettings();
    this.store.select(UserState.authState).pipe(filter(a => a === "logged in" /* AuthState.LoggedIn */), switchMap(() => this.setupSseConnection$().pipe(startWith({
      source: '',
      type: 'MEMBERSHIP_STATUS_UPDATED',
      payload: null
    }), startWith({
      source: '',
      type: 'NOTIFICATION_UPDATED',
      payload: null
    }), startWith({
      source: '',
      type: 'SYSTEM_MESSAGE_NEW',
      payload: null
    }))), filter(e => !!e)).subscribe(event => {
      this.checkMemberShipChange(event);
      this.checkNotificationUpdates(event);
      this.checkSystemMessages(event);
    });
  }
  setupUsername(googleAnalyticsActivated) {
    this.store.select(UserState.user).pipe(takeUntilDestroyed(this.destroyed)).subscribe(user => {
      this.userName = '';
      if (user.userName) {
        this.userName = user.userName;
      } else if (user.lastName && user.firstName) {
        this.userName = `${user.lastName}, ${user.firstName}`.trim();
      }
      if (googleAnalyticsActivated && user.userId) {
        const defaultRoute = this.router.config.find(value => value.path === this.router.routerState.snapshot.url);
        this.gaService.loggedInPush(defaultRoute?.redirectTo ?? '');
      }
    });
  }
  setupAppsAndLocals(canOpenBurgerMenu) {
    this.store.select(UserState.token).pipe(takeUntilDestroyed(this.destroyed)).subscribe(token => {
      if (token) {
        this.permissionService.getPermissionsForStore();
        if (canOpenBurgerMenu) {
          this.getApps();
        }
        this.registerLocales();
        this.tokenReady = true;
      }
    });
  }
  setupAssignUserContext() {
    this.store.select(UserState.tokenObject).pipe(tap(token => {
      this.currentUserContext = token?.extension_user_context;
    }), filter(token => !!token), filter(() => this.currentUserContext !== 'PORTAL_USER'), map(token => token.oid), distinctUntilChanged(), switchMap(userId => this.userService.getTempUserMembership(userId)), first()).subscribe({
      next: memberships => {
        const mem = memberships.pageEntries.find(membership => membership.state === UserMembershipState.ACCEPTED && !membership.inherited);
        if (mem) {
          this.tempUserMembership = {
            id: mem.id,
            roles: [mem.roleName ?? ''],
            name: mem.organizationName ?? ''
          };
        }
      },
      error: error => {
        this.errorHandlerService.handleError(error);
      }
    });
  }
  setupFeatureFlagConfig() {
    this.store.select(UserState.authState).pipe(takeUntilDestroyed(this.destroyed), filter(authState => authState !== "not set" /* AuthState.NotSet */), map(authState => authState === "logged in" /* AuthState.LoggedIn */)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.featureFlagService.getFeatureFlagConfigurationForStore();
      }
    });
  }
  setupUserInformations() {
    combineLatest([this.store.select(UserState.user).pipe(filter(user => !!user.userId)), this.store.select(UserState.token).pipe(filter(token => !!token))]).pipe(first(), switchMap(([user]) => {
      if (user.userId) {
        return this.userService.getUser(user.userId, true);
      } else {
        return of(null);
      }
    })).subscribe(user => {
      if (user) {
        this.store.dispatch(new UpdateGeaIdUser(user.language, user.country, user.email, user.firstName, user.lastName, user.timeZone));
      }
    });
  }
  callMembershipActionRequired() {
    this.store.select(UserState.token).pipe(filter(token => !!token), this.userActionRequired$(), first()).subscribe();
  }
  setupNotificationSettings() {
    this.store.select(UserState.token).pipe(filter(token => !!token), first()).subscribe(() => {
      this.notificationService.getNotificationSettings().pipe(takeUntilDestroyed(this.destroyed)).subscribe();
    });
  }
  setupSseConnection$() {
    return of(void 0).pipe(this.isSseEnabled$(), tap(() => this.sseService.connect()), mergeMap(() => this.sseService.sseEventStream));
  }
  isSseEnabled$() {
    return pipe(switchMap(() => this.store.select(FeatureFlagConfigurationState.features)), filter(features => !!features), map(features => 'core_portal.sse-hub.enabled' in features), tap(isEnabled => {
      if (!isEnabled) {
        this.callMembershipActionRequired();
        this.notificationsV2Service.usePollingNotification();
      }
    }), filter(isEnabled => !!isEnabled), first());
  }
  checkMemberShipChange(event) {
    of(null).pipe(filter(() => event.type === 'MEMBERSHIP_STATUS_UPDATED'), delay(2000), this.userActionRequired$()).subscribe();
  }
  checkNotificationUpdates(event) {
    of(null).pipe(filter(() => event.type === 'NOTIFICATION_UPDATED'), delay(2000), map(() => this.notificationsV2Service.fetchNotifications())).subscribe();
  }
  checkSystemMessages(event) {
    of(null).pipe(filter(() => event.type === 'SYSTEM_MESSAGE_NEW'), delay(2000), switchMap(() => this.systemMessageService.fetchSystemMessages())).subscribe();
  }
  userActionRequired$() {
    return pipe(switchMap(() => this.store.select(UserState.user)), map(res => res.userId), filter(userid => !!userid), concatMap(userId => this.userService.getActionRequired(userId)), filter(res => !!res.length), map(() => {
      this.store.dispatch(new UserActionRequired(true, ActionTypeModel.ACCEPT_MEMBERSHIP_BANNER_ACTION));
    }));
  }
  getApps() {
    this.appService.getVerifiedApps().pipe(take(1)).subscribe(appList => {
      this.appList = appList;
      this.availableApps = this.translate.instant('UI-LIB.HEADER.BURGER-MENU.AVAILABLE-APPS', {
        count: `${this.appList?.length}`
      });
    });
  }
  registerLocales() {
    this.resourcesService.languages$.subscribe(languages => {
      languages.forEach(language => {
        this.registerLocaleForLanguage(language);
      });
    });
  }
  registerLocaleForLanguage(language) {
    import(`node_modules/@angular/common/locales/${language}.mjs`).then(module => {
      registerLocaleData(module.default, language);
    }).catch(() => {
      import(`node_modules/@angular/common/locales/${language.split('-')[0]}.mjs`).then(module => {
        registerLocaleData(module.default, language);
      }).catch(() => {
        console.error(`can't find locale data for language: ${language}`);
      });
    });
  }
  static ɵfac = function HeaderNgxsService_Factory(t) {
    return new (t || HeaderNgxsService)(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.PermissionService), i0.ɵɵinject(i2.FeatureFlagService), i0.ɵɵinject(i2.ResourcesService), i0.ɵɵinject(i3.AppService), i0.ɵɵinject(i2.UserService), i0.ɵɵinject(i2.NotificationSettingsApiService), i0.ɵɵinject(i4.TranslateService), i0.ɵɵinject(i0.DestroyRef), i0.ɵɵinject(i5.GoogleAnalyticsService), i0.ɵɵinject(i6.Router), i0.ɵɵinject(i7.ErrorHandlerV2Service), i0.ɵɵinject(i8.SseStreamService), i0.ɵɵinject(i9.NotificationsV2Service), i0.ɵɵinject(i10.ActionRequiredService), i0.ɵɵinject(i11.SystemMessageService));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: HeaderNgxsService,
    factory: HeaderNgxsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HeaderNgxsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.Store
  }, {
    type: i2.PermissionService
  }, {
    type: i2.FeatureFlagService
  }, {
    type: i2.ResourcesService
  }, {
    type: i3.AppService
  }, {
    type: i2.UserService
  }, {
    type: i2.NotificationSettingsApiService
  }, {
    type: i4.TranslateService
  }, {
    type: i0.DestroyRef
  }, {
    type: i5.GoogleAnalyticsService
  }, {
    type: i6.Router
  }, {
    type: i7.ErrorHandlerV2Service
  }, {
    type: i8.SseStreamService
  }, {
    type: i9.NotificationsV2Service
  }, {
    type: i10.ActionRequiredService
  }, {
    type: i11.SystemMessageService
  }], null);
})();
